// components/edit_contract_modal.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState, useContext } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { Link } from "wouter";
import { ArrowRightIcon } from "@heroicons/react/24/outline";

import { AuthContext } from "../contexts/auth";
import { getEmployeeContractById, updateContractNotification } from "../services/api_contracts_employee";
import { getContractRecipientsDefault } from "../services/api";
import RecipientsField from "./recipients_field";
import Fetching from "./fetching";

import { splitEmails, isEmail, normalizeEmailCsv, sameEmailSet, buildDefaultEmailCsv } from "../services/utils_email";

/* ---------------- helpers ---------------- */

const toTimingNumberOrNull = (s) => {
  const raw = String(s ?? "").trim();
  if (raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return n;
};

const clampTiming = (n) => {
  if (n == null) return null;
  let x = parseInt(String(n), 10);
  if (Number.isNaN(x)) return null;
  if (x < 1) x = 1;
  if (x > 31) x = 31;
  return x;
};

const buildEffectiveNotifications = ({
  timingInput,
  emailInput,
  defaultTimingStr,
  defaultEmailCsv,
  defaultsEnabled,
  originalTiming,
  originalEmail,
}) => {
  const timingRaw = String(timingInput ?? "").trim();
  const emailRaw = String(emailInput ?? "").trim();

  // Se input vuoto:
  // - se default abilitati => usa default
  // - se default disabilitati => usa valore originale del contratto
  const timingFallback = defaultsEnabled ? defaultTimingStr : String(originalTiming ?? "").trim();
  const emailFallback = defaultsEnabled ? defaultEmailCsv : String(originalEmail ?? "").trim();

  const effectiveTiming = clampTiming(timingRaw === "" ? timingFallback : timingInput);
  const effectiveEmailCsv = normalizeEmailCsv(emailRaw === "" ? emailFallback : emailInput);

  const defaultTiming = clampTiming(defaultsEnabled ? defaultTimingStr : "");
  const defaultEmail = normalizeEmailCsv(defaultsEnabled ? defaultEmailCsv : "");

  const matchesDefault =
    defaultsEnabled && effectiveTiming === defaultTiming && sameEmailSet(effectiveEmailCsv, defaultEmail);

  return { effectiveTiming, effectiveEmailCsv, matchesDefault, defaultTiming, defaultEmail };
};

export default function EditContractModal({
  open,
  onClose,
  contract,
  onSaved,
  mode = "view",
  allowInlineEditToggle = true,
  permission = null,
}) {
  const { token, profile } = useContext(AuthContext);

  // ---------- mode ----------
  const [isEdit, setIsEdit] = useState(mode === "edit");
  useEffect(() => setIsEdit(mode === "edit"), [mode, open, contract?.id]);

  // ---------- loading/detail ----------
  const [loading, setLoading] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [full, setFull] = useState(null);

  // ---------- read-only dates ----------
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // ---------- INPUT utente ("" = default-mode) ----------
  const [timingInput, setTimingInput] = useState("");
  const [emailInput, setEmailInput] = useState("");

  const [errors, setErrors] = useState({});
  const levelValues = Object.values(profile?.level || {}).flat();
  const hasShiftsLevel = levelValues.includes("shifts");

  // ---------- suggested defaults ----------
  const [suggested, setSuggested] = useState({
    loaded: false,
    defaultDays: null,
    sendCompany: true,
    sendHandlers: true,
    companyEmail: "",
    companyLabel: "Azienda",
    handlers: [],
    othersEmails: "",
  });

  const defaultsCacheRef = useRef(new Map());
  const initDoneRef = useRef("");

  const employeeId = useMemo(() => {
    return contract?.employeeId ?? contract?.employee?.id ?? null;
  }, [contract?.employeeId, contract?.employee?.id]);

  const contractId = contract?.id ?? null;

  const employeeLabel = useMemo(() => {
    const emp = full?.employee || contract?.employee || {};
    if (emp.last_name || emp.first_name) return [emp.last_name, emp.first_name].filter(Boolean).join(" ").trim();
    return emp.cf || "—";
  }, [full, contract]);

  const title = isEdit ? "Modifica promemoria contratto" : "Dettagli contratto";

  const renderFieldError = (field) =>
    errors?.[field] ? (
      <p className="text-red-600 text-xs mt-1">
        {(Array.isArray(errors[field]) ? errors[field] : [errors[field]]).join(", ")}
      </p>
    ) : null;

  /* =========================================================
     1) FETCH SERIALIZZATO: DEFAULTS -> CONTRACT DETAIL
     ========================================================= */
  useEffect(() => {
    if (!open || !token || !employeeId || !contractId) return;

    let cancelled = false;

    (async () => {
      setErrors({});
      setBootstrapping(true);

      try {
        // ----- 1) defaults -----
        let defaultsPayload = defaultsCacheRef.current.get(employeeId);

        if (!defaultsPayload) {
          const data = await getContractRecipientsDefault(token, employeeId);
          if (cancelled) return;

          defaultsPayload = {
            defaultDays: data?.default_days ?? null,
            sendCompany: data?.send_company == null ? true : !!data.send_company,
            sendHandlers: data?.send_handlers == null ? true : !!data.send_handlers,
            companyEmail: String(data?.company_email || "").trim().toLowerCase(),
            companyLabel: String(data?.company_label || "Azienda").trim(),
            handlers: Array.isArray(data?.handlers_emails) ? data.handlers_emails : [],
            othersEmails: String(data?.others_emails || ""),
          };

          defaultsCacheRef.current.set(employeeId, defaultsPayload);
        }

        setSuggested({ loaded: true, ...defaultsPayload });

        // ----- 2) contratto -----
        const data = await getEmployeeContractById(token, employeeId, permission, contractId);
        if (cancelled) return;

        setFull(data || {});
      } catch (err) {
        if (cancelled) return;
        setSuggested((p) => ({ ...p, loaded: true }));
        setFull(contract || {});
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, token, employeeId, contractId, permission, contract]);

  /* =========================================================
     2) DEFAULTS DERIVATI
     ========================================================= */
  const defaultsEnabled = suggested.loaded && suggested.defaultDays != null;

  const defaultTimingStr = useMemo(() => {
    return defaultsEnabled ? String(suggested.defaultDays) : "";
  }, [defaultsEnabled, suggested.defaultDays]);

  const defaultEmailCsv = useMemo(() => {
    if (!suggested.loaded) return "";
    if (!defaultsEnabled) return "";
    return buildDefaultEmailCsv({
      send_company: suggested.sendCompany,
      send_handlers: suggested.sendHandlers,
      company_email: suggested.companyEmail,
      handlers_emails: suggested.handlers,
      others_emails: suggested.othersEmails,
    });
  }, [
    suggested.loaded,
    defaultsEnabled,
    suggested.sendCompany,
    suggested.sendHandlers,
    suggested.companyEmail,
    suggested.handlers,
    suggested.othersEmails,
  ]);

  /* =========================================================
     3) ORIGINAL (DB) + INIT INPUT (solo quando defaults pronti)
     ========================================================= */
  const original = useMemo(() => {
    const d = full || contract;
    if (!d) return null;

    const rawTiming = d?.notification_timing;
    const rawEmail = d?.notification_email;

    return {
      id: d.id,
      start_date: d?.start_date ? dayjs(d.start_date).format("YYYY-MM-DD") : "",
      end_date: d?.end_date ? dayjs(d.end_date).format("YYYY-MM-DD") : "",
      notification_timing: rawTiming === undefined ? null : rawTiming,
      notification_email: rawEmail == null ? null : String(rawEmail).trim(),
    };
  }, [full, contract]);

  useEffect(() => {
    if (!open) return;
    if (!suggested.loaded) return;
    if (!original) return;
    
    const initKey = [
      employeeId || "e",
      original.id || "c",
      defaultEmailCsv || "",
      String(original.notification_timing ?? ""),
      String(original.notification_email ?? ""),
    ].join("|");

    if (initDoneRef.current === initKey) return;
    initDoneRef.current = initKey;

    setStartDate(original.start_date || "");
    setEndDate(original.end_date || "");
    setErrors({});

    setTimingInput(original.notification_timing == null ? "" : String(original.notification_timing));
    setEmailInput(original.notification_email == null ? "" : String(original.notification_email));
  }, [
    open,
    suggested.loaded,
    employeeId,
    original?.id,
    original?.notification_timing,
    original?.notification_email,
    defaultEmailCsv,
  ]);

  /* =========================================================
     4) SHOWN + ENABLED
     ========================================================= */
  const shownTiming = useMemo(() => {
    const t = String(timingInput ?? "").trim();
    if (t !== "") return t;
    if (defaultsEnabled) return String(defaultTimingStr ?? "").trim();
    return String(original?.notification_timing ?? "").trim();
  }, [timingInput, defaultsEnabled, defaultTimingStr, original?.notification_timing]);

  const shownEmail = useMemo(() => {
    const e = String(emailInput ?? "").trim();
    if (e !== "") return e;
    if (defaultsEnabled) return String(defaultEmailCsv ?? "").trim();
    return String(original?.notification_email ?? "").trim();
  }, [emailInput, defaultsEnabled, defaultEmailCsv, original?.notification_email]);

  const isReminderEnabled = useMemo(() => {
    const n = toTimingNumberOrNull(shownTiming);
    return n != null && n >= 1;
  }, [shownTiming]);

  /* =========================================================
     5) VALIDATE / SAVE
     ========================================================= */
  const validate = useCallback(() => {
    if (!isEdit) return {};
    const e = {};

    const { effectiveTiming, effectiveEmailCsv } = buildEffectiveNotifications({
      timingInput,
      emailInput,
      defaultTimingStr,
      defaultEmailCsv,
      defaultsEnabled,
      originalTiming: original?.notification_timing,
      originalEmail: original?.notification_email,
    });

    const timingEnabled = effectiveTiming != null && effectiveTiming >= 1;

    if (timingEnabled && !endDate) {
      e.end_date = ["Devi impostare una data fine contratto per usare il promemoria."];
    }

    if (timingEnabled && !effectiveEmailCsv) {
      e.notification_email = ["Devi inserire almeno un indirizzo email per inviare il promemoria."];
    }

    if (String(timingInput || "").trim() !== "") {
      const n = clampTiming(timingInput);
      if (n == null) {
        e.notification_timing = ["Il promemoria deve essere un numero tra 1 e 31, oppure vuoto per usare il default."];
      }
    }

    if (timingEnabled && effectiveEmailCsv) {
      const invalids = splitEmails(effectiveEmailCsv).filter((x) => !isEmail(x));
      if (invalids.length) e.notification_email = [`Email non valide: ${invalids.join(", ")}`];
    }

    return e;
  }, [
    isEdit,
    timingInput,
    emailInput,
    defaultTimingStr,
    defaultEmailCsv,
    defaultsEnabled,
    original?.notification_timing,
    original?.notification_email,
    endDate,
  ]);

  const handleCancelEdit = () => {
    if (!original) return;
    setIsEdit(false);

    setStartDate(original.start_date || "");
    setEndDate(original.end_date || "");
    setTimingInput(original.notification_timing == null ? "" : String(original.notification_timing));
    setEmailInput(original.notification_email == null ? "" : String(original.notification_email));
    setErrors({});
  };

  const handleSave = async () => {
    if (!token || !employeeId || !contractId || !original || !isEdit) return;

    setErrors({});
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }

    const patch = {};

    const { effectiveTiming, effectiveEmailCsv, matchesDefault } = buildEffectiveNotifications({
      timingInput,
      emailInput,
      defaultTimingStr,
      defaultEmailCsv,
      defaultsEnabled,
      originalTiming: original.notification_timing,
      originalEmail: original.notification_email,
    });

    if (matchesDefault) {
      patch.notification_timing = null;
      patch.notification_email = null;
    } else {
      patch.notification_timing = effectiveTiming ?? null;
      patch.notification_email = effectiveEmailCsv ? normalizeEmailCsv(effectiveEmailCsv) : null;
    }

    // evita patch inutile
    const origTimingNorm = original.notification_timing == null ? null : clampTiming(original.notification_timing);
    const patchTimingNorm = patch.notification_timing == null ? null : clampTiming(patch.notification_timing);
    if (patchTimingNorm === origTimingNorm) delete patch.notification_timing;

    const origEmailNorm = original.notification_email == null ? null : normalizeEmailCsv(original.notification_email);
    const patchEmailNorm = patch.notification_email == null ? null : normalizeEmailCsv(patch.notification_email);
    if (patchEmailNorm === origEmailNorm) delete patch.notification_email;

    if (Object.keys(patch).length === 0) {
      onClose?.();
      return;
    }

    setLoading(true);
    try {
      const updated = await updateContractNotification(token, employeeId, contractId, patch);
      onSaved?.({ ...updated, id: contractId });

      Swal.fire({ icon: "success", title: "Promemoria aggiornato", timer: 1500, showConfirmButton: false });
      onClose?.();
    } catch (err) {
      if (err?.response?.data && typeof err.response.data === "object") setErrors(err.response.data);
      else setErrors({ generico: [err?.message || "Errore imprevisto."] });
    } finally {
      setLoading(false);
    }
  };

  const companyEmail = suggested.companyEmail;
  const companyName = suggested.companyLabel || profile?.company?.name || "Azienda";
  const handlers = suggested.handlers;
  const othersEmails = suggested.othersEmails;

  const showFetching = bootstrapping || !suggested.loaded || !original;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle component="div" className="flex bg-indigo-200 justify-between">
        <div className="font-semibold text-indigo-800">{title}</div>

        {contract?.employee?.cf && (
          <Link
            to={`/dipendenti/${contract.employee.cf}/edit?tab=2`}
            className="justify-center flex items-center rounded-md bg-indigo-600 px-2 py-1 uppercase text-xs font-semibold text-white shadow-sm hover:bg-indigo-500"
          >
            <ArrowRightIcon className="h-4 w-4 inline-block me-2" />
            Vai al contratto
          </Link>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {showFetching ? (
          <Fetching />
        ) : (
          <>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Dipendente</label>
              <div className="h-10 flex items-center px-3 rounded-md bg-gray-50 shadow-sm border border-gray-200 text-gray-700 text-sm">
                {employeeLabel}
              </div>
            </div>

            {/* Date solo lettura */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="mb-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Data inizio</label>
                <input
                  type="date"
                  value={startDate || ""}
                  readOnly
                  disabled
                  className="shadow border rounded w-full py-2 px-3 bg-gray-50 border-gray-200 text-gray-700"
                />
              </div>

              <div className="mb-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Data fine</label>
                <input
                  type="date"
                  value={endDate || ""}
                  readOnly
                  disabled
                  className="shadow border rounded w-full py-2 px-3 bg-gray-50 border-gray-200 text-gray-700"
                />
                {renderFieldError("end_date")}
              </div>
            </div>

            {/* Promemoria */}
            <div className="mt-3">
              <label className="block text-sm font-semibold text-gray-700 mb-0">Promemoria scadenza</label>
              <div className="text-gray-500 font-normal text-xs mb-1">
                {suggested.defaultDays != null
                  ? "Giorni da 1 a 31, campo vuoto per l'impostazione predefinita"
                  : "Giorni da 1 a 31"}
                {defaultTimingStr && <span className="pl-2">({defaultTimingStr} giorni)</span>}
              </div>

              {isEdit ? (
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder={defaultTimingStr ? `${defaultTimingStr} giorni` : "Inserire giorni"}
                  value={timingInput}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v !== "" && !/^\d+$/.test(v)) return;
                    setTimingInput(v);
                    setErrors((prev) => {
                      const next = { ...prev };
                      delete next.notification_timing;
                      delete next.notification_email;
                      delete next.end_date;
                      return next;
                    });
                  }}
                  onBlur={() => {
                    const raw = String(timingInput || "").trim();
                    if (raw === "") return;
                    let n = parseInt(raw, 10);
                    if (Number.isNaN(n)) n = 7;
                    if (n < 1) n = 1;
                    if (n > 31) n = 31;
                    setTimingInput(String(n));
                  }}
                  className="w-full h-10 px-3 rounded-md shadow-sm border border-gray-500
                    hover:border-indigo-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200
                    text-gray-700 text-md bg-white"
                />
              ) : (
                <div className="h-10 flex items-center px-3 rounded-md bg-gray-50 shadow-sm border border-gray-200 text-gray-700 text-md">
                  {original?.notification_timing == null
                    ? defaultTimingStr
                      ? `${defaultTimingStr} giorni (predefinito)`
                      : "Disabilitato"
                    : `${original.notification_timing} giorni`}
                </div>
              )}

              {renderFieldError("notification_timing")}
            </div>

            {/* Email promemoria */}
            {isReminderEnabled && (
              <div className="mt-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email di notifica scadenza</label>

                <div className="border border-gray-200 rounded-md">
                  <RecipientsField
                    token={token}
                    value={shownEmail}
                    onChange={(csv) => {
                      if (!isEdit) return;
                      setEmailInput(csv);
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.notification_email;
                        return next;
                      });
                    }}
                    disabled={false}
                    isEdit={isEdit}
                    openDirection="down"
                    companyEmail={companyEmail}
                    companyName={companyName}
                    handlers={handlers}
                    othersEmails={othersEmails}
                    className="bg-gray-100 rounded-md py-2"
                    hasShiftsLevel={hasShiftsLevel}
                  />
                </div>

                {renderFieldError("notification_email")}
              </div>
            )}

            {errors?.generico && (
              <p className="text-red-600 text-xs mt-3">
                {(Array.isArray(errors.generico) ? errors.generico : [errors.generico]).join(", ")}
              </p>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", gap: 8, p: 2 }}>
        {isEdit ? (
          <>
            <button
              onClick={handleCancelEdit}
              disabled={loading || showFetching}
              className="uppercase bg-gray-200 text-gray-700 text-sm font-semibold px-4 py-2 rounded-md hover:bg-gray-300 w-28"
            >
              Annulla
            </button>
            <button
              onClick={handleSave}
              disabled={loading || showFetching}
              className="uppercase font-semibold text-white text-sm px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 w-28"
            >
              {loading ? "Salvataggio…" : "Salva"}
            </button>
          </>
        ) : (
          <>
            <button
              onClick={onClose}
              disabled={showFetching}
              className="uppercase bg-gray-200 text-gray-700 text-sm font-semibold px-4 py-2 rounded-md hover:bg-gray-300 w-28"
            >
              Chiudi
            </button>
            {allowInlineEditToggle && (
              <button
                onClick={() => {
                  setIsEdit(true);
                  setErrors({});
                }}
                disabled={showFetching}
                className="uppercase font-semibold text-white text-sm px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 w-28"
              >
                Modifica
              </button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}